import { ButtonProps } from '@mui/material/Button';

// ----------------------------------------------------------------------
// JButton's `variant` prop conflates MUI's two axes — `variant` (contained /
// outlined / text / soft) and `color` (primary / secondary / error / …) — into a
// single token. This maps it back onto the pair, so the look comes entirely from
// the MuiButton styleOverrides in overrides/components/button.tsx.
//
// It used to come from src/theme/buttonTheme.ts instead, which returned `sx`
// objects built from a module-level import of the v1 palette. Injected through
// the `sx` prop, those beat the theme's styleOverrides, so every JButton in both
// products rendered v1 colors (orange gradients, `background: 'black'`) no matter
// which theme was active. Nothing here emits colors any more.
// ----------------------------------------------------------------------

export type ButtonVariant =
  | 'primary'
  | 'contained'
  | 'secondary'
  | 'outlined'
  | 'text'
  | 'soft'
  | 'error'
  | 'warning'
  | 'info'
  | 'success';

type ButtonLook = {
  variant: NonNullable<ButtonProps['variant']>;
  color: NonNullable<ButtonProps['color']>;
};

export const BUTTON_VARIANT_MAP: Record<ButtonVariant, ButtonLook> = {
  primary: { variant: 'contained', color: 'primary' },
  // v1's `contained` was a hardcoded `background: 'black'`, which has no separate
  // v2 token — deepNavy 900 (#081220) is what it was approximating. Collapsed onto
  // primary rather than inventing a second near-black.
  contained: { variant: 'contained', color: 'primary' },
  // Peach, not the lilac in palette.secondary — see the SECONDARY comment in palette-v2.ts.
  secondary: { variant: 'contained', color: 'secondary' },
  outlined: { variant: 'outlined', color: 'primary' },
  text: { variant: 'text', color: 'primary' },
  // Neutral soft. `soft` + `secondary` would fall through to the generic colorStyle
  // branch and render lilac, contradicting "secondary button = peach".
  soft: { variant: 'soft', color: 'primary' },
  error: { variant: 'contained', color: 'error' },
  // info / success / warning have no bespoke v2 matrix: the override only *adds* to
  // MUI's own contained styles, which already resolve palette[color].main +
  // contrastText — both now spelled out in palette-v2.ts.
  warning: { variant: 'contained', color: 'warning' },
  info: { variant: 'contained', color: 'info' },
  success: { variant: 'contained', color: 'success' },
};

export const DEFAULT_BUTTON_VARIANT: ButtonVariant = 'contained';

export function buttonLook(variant: ButtonVariant | undefined): ButtonLook {
  return BUTTON_VARIANT_MAP[variant as ButtonVariant] ?? BUTTON_VARIANT_MAP[DEFAULT_BUTTON_VARIANT];
}
